type Candle = {
  high: number;
  low: number;
  open: number;
  close: number;
};

type ChartMarker = {
  no: number;
  top: string;
  left: string;
};

type AnalysisCandleChartProps = {
  candles: Candle[];
  support?: number;
  resistance?: number;
  showTrendline?: boolean;
  markers?: ChartMarker[];
  ariaLabel: string;
};

/** Maps a 0-100 price value to a vertical percentage inside the plot area. */
function y(value: number) {
  return 5 + (1 - value / 100) * 90;
}

export default function AnalysisCandleChart({
  candles,
  support,
  resistance,
  showTrendline = true,
  markers = [],
  ariaLabel,
}: AnalysisCandleChartProps) {
  const top = (candle: Candle) => Math.min(y(candle.open), y(candle.close));
  const bodyHeight = (candle: Candle) => Math.max(2.2, Math.abs(y(candle.open) - y(candle.close)));

  return (
    <div role="img" aria-label={ariaLabel} className="relative h-full w-full overflow-hidden rounded-lg bg-background-50">
      <div className="pointer-events-none absolute inset-0 grid-lines opacity-40" />

      {[18, 40, 62, 84].map((line) => (
        <span
          key={line}
          aria-hidden="true"
          className="absolute left-0 right-12 border-t border-background-200/80"
          style={{ top: `${line}%` }}
        />
      ))}

      {typeof resistance === 'number' && (
        <>
          <span
            aria-hidden="true"
            className="absolute left-0 right-14 border-t border-dashed border-accent-400"
            style={{ top: `${y(resistance)}%` }}
          />
          <span
            className="absolute right-0 -translate-y-1/2 rounded-sm bg-accent-100 px-1.5 py-1 text-[10px] font-semibold leading-none text-accent-800"
            style={{ top: `${y(resistance)}%` }}
          >
            แนวต้าน
          </span>
        </>
      )}

      {typeof support === 'number' && (
        <>
          <span
            aria-hidden="true"
            className="absolute left-0 right-14 border-t border-dashed border-primary-400"
            style={{ top: `${y(support)}%` }}
          />
          <span
            className="absolute right-0 -translate-y-1/2 rounded-sm bg-primary-100 px-1.5 py-1 text-[10px] font-semibold leading-none text-primary-800"
            style={{ top: `${y(support)}%` }}
          >
            แนวรับ
          </span>
        </>
      )}

      {showTrendline && (
        <span
          aria-hidden="true"
          className="absolute left-[3%] w-[76%] origin-left -rotate-[23deg] border-t-2 border-accent-500/70"
          style={{ top: `${y(28)}%` }}
        />
      )}

      <div className="absolute inset-y-1 left-1 right-12 flex items-stretch gap-[3px]">
        {candles.map((candle, index) => {
          const up = candle.close >= candle.open;
          return (
            <div key={index} className="relative h-full flex-1">
              <span
                aria-hidden="true"
                className="absolute left-1/2 w-px -translate-x-1/2 bg-foreground-400/60"
                style={{ top: `${y(candle.high)}%`, height: `${y(candle.low) - y(candle.high)}%` }}
              />
              <span
                aria-hidden="true"
                className={`absolute left-1/2 w-full max-w-[15px] -translate-x-1/2 rounded-[1px] ${up ? 'bg-primary-500' : 'bg-accent-500'}`}
                style={{ top: `${top(candle)}%`, height: `${bodyHeight(candle)}%` }}
              />
            </div>
          );
        })}
      </div>

      {markers.map((marker, index) => (
        <span
          key={marker.no}
          className="absolute -translate-x-1/2 -translate-y-1/2"
          style={{ top: marker.top, left: marker.left }}
        >
          <span className="flex h-6 w-6 items-center justify-center rounded-full border border-background-200 bg-background-50 font-num text-[11px] font-semibold text-foreground-900 transition-colors">
            {marker.no}
          </span>
          <span className="sr-only">{`จุดอ้างอิงที่ ${index + 1}`}</span>
        </span>
      ))}
    </div>
  );
}